import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import {
  MilitaryTech as RankIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  PublicOutlined as OtrixverseIcon,
} from '@mui/icons-material';
import { GOLD } from 'common/theme/button/premiumButtonSx';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * RankingExplanation - Componente que explica cómo funciona el sistema de puntos.
 * Monocromo: superficies off-black, un único acento dorado (íconos, barra superior, chips).
 */
function RankingExplanation() {
  const { t } = useTranslation(['common']);

  const factors = [
    {
      icon: <OtrixverseIcon sx={{ fontSize: '2rem' }} />,
      title: t('ranking.factors.monthlyExercises.title', { ns: 'common' }),
      percentage: null,
      description: t('ranking.factors.monthlyExercises.description', { ns: 'common' }),
    },
    {
      icon: <CalendarIcon sx={{ fontSize: '2rem' }} />,
      title: t('ranking.factors.yearAccumulation.title', { ns: 'common' }),
      percentage: null,
      description: t('ranking.factors.yearAccumulation.description', { ns: 'common' }),
    },
    {
      icon: <RankIcon sx={{ fontSize: '2rem' }} />,
      title: t('ranking.factors.yourRank.title', { ns: 'common' }),
      percentage: null,
      description: t('ranking.factors.yourRank.description', { ns: 'common' }),
    },
  ];

  return (
    <Box sx={{ mt: { xs: 3, sm: 4, md: 6 }, mb: { xs: 2, sm: 3, md: 4 } }}>
      {/* Título de la sección */}
      <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <InfoIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }, color: 'var(--otx-gold-text)' }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            letterSpacing: 0.5,
            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
          }}
        >
          {t('ranking.howItWorks', { ns: 'common', defaultValue: '¿Cómo funciona el sistema de puntos?' })}
        </Typography>
      </Stack>

      {/* Grid de factores */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        {factors.map((factor) => (
          <Grid item xs={12} sm={6} md={4} key={factor.title}>
            <Card
              sx={{
                height: '100%',
                backgroundColor: 'surface.content',
                border: '1px solid',
                borderColor: 'hairline.main',
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
                transition: `transform 0.25s ${EASE}, box-shadow 0.25s ${EASE}, border-color 0.25s ${EASE}, background-color 0.25s ${EASE}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  backgroundColor: 'surface.hover',
                  borderColor: 'hairline.strong',
                  boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow-hover)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: { xs: '3px', sm: '4px' },
                  background: GOLD,
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                {/* Icono y Porcentaje */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      width: { xs: 48, sm: 52, md: 56 },
                      height: { xs: 48, sm: 52, md: 56 },
                      borderRadius: { xs: 1.5, sm: 2 },
                      backgroundColor: 'gold.soft',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--otx-gold-text)',
                      '& svg': {
                        fontSize: { xs: '1.75rem', sm: '1.875rem', md: '2rem' },
                      },
                    }}
                  >
                    {factor.icon}
                  </Box>
                  {factor.percentage && (
                    <Chip
                      label={factor.percentage}
                      sx={{
                        backgroundColor: 'gold.soft',
                        color: 'var(--otx-gold-text)',
                        fontWeight: 800,
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        height: { xs: 24, sm: 26, md: 28 },
                      }}
                    />
                  )}
                </Stack>

                {/* Título */}
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 700,
                    mb: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '0.9375rem', sm: '0.9675rem', md: '1rem' },
                  }}
                >
                  {factor.title}
                </Typography>

                {/* Descripción */}
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.8125rem', sm: '0.84375rem', md: '0.875rem' },
                  }}
                >
                  {factor.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ borderColor: 'hairline.main', my: { xs: 2, sm: 3, md: 4 } }} />

      {/* Info adicional */}
      <Card
        sx={{
          backgroundColor: 'surface.overlay',
          border: '1px solid',
          borderColor: 'hairline.main',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
            <ScheduleIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' }, color: 'var(--otx-gold-text)' }} />
            <Box>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: { xs: '0.9375rem', sm: '0.96875rem', md: '1rem' },
                }}
              >
                {t('ranking.updateSchedule', { ns: 'common', defaultValue: '¿Cuándo se actualiza?' })}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.8125rem', sm: '0.84375rem', md: '0.875rem' },
                }}
              >
                {t('ranking.updateInfo', { ns: 'common', defaultValue: 'La barra del mes se reinicia el día 1, pero tu puntaje acumulado del año se conserva. El ranking se recalcula cada noche con tus rendimientos OTRIXVERSE.' })}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default RankingExplanation;
